<template>
  <div role="main" class="container pb-3 pt-0">
    <div class="d-flex justify-content-between mb-3 text-left">
      <h2 class="font-weight-bold mb-0 titulo-cap">Cursos del mes</h2>
      <div class="d-flex align-items-center">
        <button
          :class="[
            'btn btn-icon mr-2',
            esMesActual ? 'btn-primary' : 'btn-outline-primary',
          ]"
          @click="irHoy"
          aria-label="Calendario"
        >
          Hoy
        </button>
        <router-link
          class="btn btn-outline-primary btn-icon"
          :to="{ path: '/home-cap', query: $route.query }"
          aria-label="Ver lista diaria"
        >
          <i class="fas fa-list fa-lg"></i>
        </router-link>
      </div>
    </div>

    <div class="calendar-wrapper p-2">
      <vc-calendar
        :from-page="page"
        @update:from-page="page = $event"
        :attributes="vcAttributes"
        :first-day-of-week="1"
        :masks="masks"
        locale="es"
        is-expanded
      >
        <template #day-content="{ day }">
          <div
            class="day-cell"
            role="button"
            tabindex="0"
            @click="goToDay(day.date)"
            @keydown.enter="goToDay(day.date)"
          >
            <div class="day-num">{{ day.day }}</div>

            <div
              v-if="countFor(day.date)"
              class="dots-wrap"
              :title="countFor(day.date) + ' evento(s)'"
            >
              <span
                v-for="(ev, idx) in dotsFor(day.date)"
                :key="idx"
                class="dot"
                :class="'dot--' + ev.estado_capacitador"
              />
              <small v-if="extraCount(day.date)" class="more"
                >+{{ extraCount(day.date) }}</small
              >
            </div>
          </div>
        </template>
      </vc-calendar>
    </div>

    <div v-if="loading" class="spinner-overlay">
      <div
        class="spinner-border text-primary"
        style="width: 3rem; height: 3rem"
        role="status"
      >
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <div
      v-else-if="error"
      class="alert alert-danger d-flex justify-content-between align-items-center"
    >
      <span>{{ error }}</span>
      <button class="btn btn-sm btn-light" @click="fetchMonth">
        Reintentar
      </button>
    </div>

    <FlashMessage
      :style="{ position: 'fixed', zIndex: 9999, fontSize: '18px' }"
    />
  </div>
</template>

<script>
import axiosInstance from "@/services/axiosInstance";

// util: YYYY-MM-DD local
function ymdLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// adapta cualquier respuesta del backend a { 'YYYY-MM-DD': Array(n) }
function buildEventMap(data) {
  const map = {};
  if (Array.isArray(data?.dias)) {
    for (const d of data.dias) {
      if (Array.isArray(d.items)) {
        map[d.fecha] = d.items.map((it) => ({
          edo: Number(it.edo) === 1 ? 1 : 0,
          estado_capacitador:
            it.estado_capacitador ||
            (Number(it.edo) === 1 ? "finalizado" : "por_confirmar"),
        }));
      } else if (typeof d.n === "number") {
        // Caso legacy: si solo viene 'n', rellena con edo=0
        map[d.fecha] = Array.from({ length: d.n }, () => ({
          edo: 0,
          estado_capacitador: "por_confirmar",
        }));
      }
    }
  } else if (Array.isArray(data?.fechas)) {
    // Caso legacy: una fecha = un evento edo=0
    for (const f of data.fechas) {
      map[f] = [{ edo: 0, estado_capacitador: "por_confirmar" }];
    }
  }
  return map;
}

export default {
  name: "VistaCalendario",
  data() {
    // const t = new Date()
    return {
      page: null,
      masks: { title: "MMMM YYYY", weekdays: "W", input: "YYYY-MM-DD" },
      loading: false,
      error: "",
      eventMap: {}, // {'YYYY-MM-DD': Array(n)}
      limitPerDay: 3, // máx. de puntitos visibles
      maxDots: 3,
    };
  },

  computed: {
    vcAttributes() {
      const todayAttr = {
        key: "hoy",
        highlight: { color: "red", fillMode: "light" }, // personaliza
        dates: new Date(),
      };

      const days = Object.keys(this.eventMap);
      const dotsAttr = days.length
        ? {
            key: "has-events",
            dot: { color: "#103d82" },
            dates: days.map((d) => new Date(d)),
          }
        : null;
      return dotsAttr ? [todayAttr, dotsAttr] : [todayAttr];
    },
    esMesActual() {
      const hoy = new Date();
      return (
        this.page &&
        this.page.year === hoy.getFullYear() &&
        this.page.month === hoy.getMonth() + 1
      );
    },
  },

  watch: {
    page: {
      deep: true,
      handler() {
        this.fetchMonth();
      },
    },
  },

  mounted() {
    const p = this.pageFromRoute(this.$route);
    if (p) this.page = p;
  },

  methods: {
    irHoy() {
      const hoy = new Date();
      this.page = { year: hoy.getFullYear(), month: hoy.getMonth() + 1 };
    },
    // lee {year,month} desde route (?mes o ?fecha)
    pageFromRoute(route) {
      const q = route?.query || {};
      if (q.mes && /^\d{4}-\d{2}$/.test(q.mes)) {
        const [y, m] = q.mes.split("-").map(Number);
        return { year: y, month: m };
      }
    },
    async fetchMonth() {
      this.loading = true;
      this.error = "";
      try {
        const m = String(this.page.month).padStart(2, "0");
        const mes = `${this.page.year}-${m}`;
        const { data } = await axiosInstance.get("/cap/calendario", {
          params: { mes },
        });
        this.eventMap = buildEventMap(data);
      } catch (e) {
        this.error = "No pudimos cargar los eventos del mes.";
      } finally {
        this.loading = false;
      }
    },

    goToDay(date) {
      const d = ymdLocal(date);
      this.$router.push({
        path: "/home-cap",
        query: {
          fecha: d,
          mes: `${this.page.year}-${String(this.page.month).padStart(2, "0")}`,
        },
      });
    },
    keyFor(dateObj) {
      // V-Calendar te pasa day.date (Date). Formatea a 'YYYY-MM-DD'
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    eventsFor(dateObj) {
      return this.eventMap[this.keyFor(dateObj)] || [];
    },
    countFor(dateObj) {
      return this.eventsFor(dateObj).length;
    },
    dotsFor(dateObj) {
      // Solo los primeros `maxDots` para dibujar
      return this.eventsFor(dateObj).slice(0, this.maxDots);
    },
    extraCount(dateObj) {
      const total = this.countFor(dateObj);
      return total > this.maxDots ? total - this.maxDots : 0;
    },
  },
};
</script>

<style scoped>
.day-cell {
  display: flex;
  flex-direction: column;
  align-items: center; /* centra horizontal */
  justify-content: flex-start;
  min-height: 72px;
  cursor: pointer;
}

.day-num {
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 4px;
}

.dots-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
}
.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #103d82;
  display: inline-block;
}
.more {
  color: #6c757d;
}
.dot--ok {
  background: #16a34a;
}
.dot--por_confirmar {
  background: #f59e0b;
}
.dot--confirmado {
  background: #2563eb;
}
.dot--finalizado {
  background: #16a34a;
}
.dot--historico {
  background: #6b7280;
}
@media (min-width: 992px) {
  .dot {
    width: 7px;
    height: 7px;
  }
}
</style>
